import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

// Firebase
import { db } from '../../firebase/config';
import { collection, getDocs, doc, getDoc } from 'firebase/firestore';
import { saveToFirestore } from '../../firebase/cleanPayload';

// Ecosistema centralizado de Seguros (Pilares del Canvas)
import { useSegurosData } from '../../context/SegurosDataContext';
import { parseCurrency, parsePercent, formatCurrencyForInput } from '../../utils/segurosUtils';
import { CurrencyInput, PercentInput } from '../../utils/FormInputs';

// --- ICONOS ---
const IconArrowLeft = () => <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M19 12H5M12 19l-7-7 7-7"/></svg>;
const IconSend = () => <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 2L11 13M22 2l-7 20-4-9-9-4Z"/></svg>;

// Helpers internos específicos
const formatPercentForInput = (value) => {
    if (value === undefined || value === null || value === '') return '';
    return String(value).replace('.', ',');
};

const generarNumeroCotizacion = () => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    let result = '';
    for (let i = 0; i < 6; i++) {
        result += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return `COT-${result}`; 
};

/**
 * FormularioPedidoCotizacionART - Circuito de Pedidos de Productor con Impacto Real en Firestore y Alerta por Mail
 */
const FormularioPedidoCotizacionART = ({ entityData: propsEntity, isProspect: propsIsProspect, onSuccess, onCancel }) => {
    const navigate = useNavigate();

    // Consumimos el contexto global de maestros de seguros
    const { dbMasters } = useSegurosData();

    // Identidad del Cliente inyectada síncronamente desde el buscador principal
    const clientInfo = propsEntity;
    const isProspect = propsIsProspect || clientInfo?.tipoEntidad === 'PROSPECTO';

    const defaultFormData = {
        numeroCotizacion: '',
        contratoOrigenId: '', // ID del contrato que se desea recotizar
        nContratoOrigen: '',  // Número visible del contrato de origen
        fechaCarga: new Date().toISOString().split('T')[0],
        tipoGestion: '',
        afiliacion: false,    // Campo de Afiliación para Venta Nueva
        estado: 'Solicitud de Cotización', // Valor por defecto unívoco requerido
        ciiu: '',
        capitas: '',
        masaSalarial: '',
        alicuotaActual: '',
        artActual: '',
        artActualId: '',
        vendedor: '',
        observaciones: ''
    };

    const [formData, setFormData] = useState(defaultFormData);
    const [listaContratosCliente, setListaContratosCliente] = useState([]);
    const [isLoadingContratos, setIsLoadingContratos] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [errorMsg, setErrorMsg] = useState('');

    // --- DESCARGA DEL LEGAJO COMPLETO EN SEGUNDO PLANO ---
    const [fullClient, setFullClient] = useState(null);

    useEffect(() => {
        const fetchFullClient = async () => {
            if (!clientInfo?.id) return;
            try {
                const colPath = isProspect ? 'prospectos' : 'clientes';
                const docRef = doc(db, `public/data/${colPath}`, clientInfo.id);
                const snap = await getDoc(docRef);
                if (snap.exists()) {
                    setFullClient({ id: snap.id, ...snap.data() });
                }
            } catch (e) {
                console.error("Error al descargar el legajo del cliente/prospecto:", e);
            }
        };
        fetchFullClient();
    }, [clientInfo?.id, isProspect]);

    const activeClient = useMemo(() => {
        return { ...clientInfo, ...fullClient };
    }, [clientInfo, fullClient]);

    // --- MEMORIZACIÓN DE MAESTROS DESDE EL CONTEXTO GLOBAL ---
    const companiasART = useMemo(() => {
        return (dbMasters?.companias || [])
            .filter(d => d.grupo === 'ART' || d.riesgo === 'ART')
            .sort((a, b) => (a.nombre || '').localeCompare(b.nombre || ''));
    }, [dbMasters?.companias]);

    const vendedores = useMemo(() => {
        return (dbMasters?.colaboradores || [])
            .map(d => d.nombre)
            .filter(Boolean)
            .sort((a, b) => a.localeCompare(b));
    }, [dbMasters?.colaboradores]);

    useEffect(() => {
        if (activeClient) {
            setFormData(prev => ({
                ...prev,
                numeroCotizacion: generarNumeroCotizacion(),
                vendedor: activeClient.vendedor || ''
            }));
        }
    }, [activeClient]);

    // Carga de contratos existentes para recotizaciones
    useEffect(() => {
        const fetchContratos = async () => {
            if (!isProspect && activeClient?.id) {
                setIsLoadingContratos(true);
                try {
                    const snap = await getDocs(collection(db, `public/data/clientes/${activeClient.id}/contratos`));
                    setListaContratosCliente(snap.docs.map(d => ({ id: d.id, ...d.data() })));
                } catch (e) {
                    console.error("Error cargando contratos vinculados:", e);
                } finally {
                    setIsLoadingContratos(false);
                }
            }
        };
        fetchContratos();
    }, [activeClient, isProspect]);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        
        setFormData(prev => {
            const val = type === 'checkbox' ? checked : value;
            const newState = { ...prev, [name]: val };

            // Resetear afiliación si cambia el tipo de gestión
            if (name === 'tipoGestion' && value !== 'Venta Nueva') {
                newState.afiliacion = false;
            }

            if (name === 'contratoOrigenId') {
                if (value) {
                    const con = listaContratosCliente.find(c => c.id === value);
                    if (con) {
                        newState.contratoOrigenId = value;
                        newState.nContratoOrigen = con.nContrato || '';
                        newState.artActual = con.compania || prev.artActual;
                        newState.artActualId = con.companiaId || '';
                        newState.vendedor = con.vendedor || prev.vendedor;
                        newState.ciiu = con.ciiu || '';
                        newState.alicuotaActual = con.alicuota ? formatPercentForInput(con.alicuota) : prev.alicuotaActual;
                    }
                } else {
                    newState.contratoOrigenId = '';
                    newState.nContratoOrigen = '';
                    newState.artActual = '';
                    newState.artActualId = '';
                    newState.ciiu = '';
                    newState.alicuotaActual = '';
                }
            }

            if (name === 'artActual') {
                const found = companiasART.find(c => c.nombre === value);
                newState.artActualId = found ? found.id : '';
            }

            return newState;
        });
    };

    const handleCurrencyChange = (e, fieldName) => {
        let val = e.target.value.replace(/\D/g, ''); 
        if (val) {
            const num = Number(val) / 100;
            setFormData(prev => ({ ...prev, [fieldName]: formatCurrencyForInput(num) }));
        } else {
            setFormData(prev => ({ ...prev, [fieldName]: '' }));
        }
    };

    const handlePercentChange = (e) => {
        const { name, value } = e.target;
        let val = value.replace(/\./g, ',').replace(/[^0-9,]/g, '');
        const parts = val.split(',');
        if (parts.length > 2) val = parts[0] + ',' + parts.slice(1).join('');
        setFormData(prev => ({ ...prev, [name]: val }));
    };

    // --- ACCIÓN CENTRAL: IMPACTO EN FIRESTORE + DISPARO DE MAIL ---
    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!activeClient?.id) {
            setErrorMsg('Error: No se ha detectado la identidad del cliente o prospecto.');
            return;
        }

        setIsSubmitting(true);
        setErrorMsg('');

        try {
            let ciaId = '';
            if (formData.artActual) {
                const targetCia = companiasART.find(c => c.nombre === formData.artActual);
                if (targetCia) ciaId = targetCia.id;
            }

            // ◄◄◄ MAPEO DE ESTRUCTURA COMERCIAL GARANTIZADO SINCRO CON MODALPROSPECTO ◄◄◄
            const resolvedCanal = activeClient.canal || '';
            const resolvedCanalNombreId = activeClient.canalNombreId || activeClient.canalId || '';
            const resolvedCanalNombre = activeClient.canalNombre || activeClient.canalDisplay || activeClient.canal || '';

            const resolvedSubcanalNombreId = activeClient.subcanalNombreId || activeClient.subcanalId || '';
            const resolvedSubcanalNombre = activeClient.subcanalNombre || activeClient.subcanal || '';

            const cleanPayload = {
                numeroCotizacion: formData.numeroCotizacion,
                contratoOrigenId: formData.contratoOrigenId || '',
                nContratoOrigen: formData.nContratoOrigen || '',
                fechaCarga: formData.fechaCarga,
                tipoGestion: formData.tipoGestion,
                afiliacion: formData.tipoGestion === 'Venta Nueva' ? Boolean(formData.afiliacion) : false,
                estado: formData.estado,
                ciiu: formData.ciiu || '',
                capitas: parseInt(formData.capitas, 10) || 0,
                masaSalarial: parseCurrency(formData.masaSalarial),
                alicuotaActual: parsePercent(formData.alicuotaActual),
                artActual: formData.artActual || '',
                artActualId: ciaId,
                vendedor: formData.vendedor || activeClient.vendedor || '',
                observaciones: formData.observaciones || '',
                
                // DATOS DEL CLIENTE / PROSPECTO
                clienteId: activeClient.id,
                clienteNombre: (activeClient.nombre || '').trim().toUpperCase(),
                clienteApellido: activeClient.persona === 'JURIDICA' ? '' : (activeClient.apellido || '').trim().toUpperCase(),
                clienteCuit: activeClient.cuit || '',
                clienteDni: activeClient.dni || '',
                
                // ESTRUCTURA COMERCIAL RESUELTA SIN PÉRDIDA DE PROPIEDADES
                canal: resolvedCanal,                       // ej. "Pas"
                canalNombreId: resolvedCanalNombreId,       // ID del canal en dbMasters
                canalNombre: resolvedCanalNombre,           // Nombre visible del canal
                subcanalNombreId: resolvedSubcanalNombreId, // ID del subcanal en dbMasters
                subcanalNombre: resolvedSubcanalNombre,     // Nombre visible del subcanal
                
                tipoEntidad: isProspect ? 'PROSPECTO' : 'CLIENTE',
                cotizaciones: [] 
            };

            const folderPath = isProspect ? 'prospectos' : 'clientes';
            const firestorePath = `public/data/${folderPath}/${activeClient.id}/cotizaciones_art`;

            // 1. Impactamos el registro en Firebase
            await saveToFirestore(firestorePath, cleanPayload);

            // 2. Disparador de mail
            const API_URL = "https://script.google.com/macros/s/AKfycbxJtiWzKj66lMAN6is-Cg9wBjxdudZ07QwRzAlcCAN7jEGKoNGjgAtajVs7AH0WtZ6p/exec"; 

            const emailPayload = {
                destinatario: "art@segurosib.com",
                asunto: `🚨 Solicitud de Cotización ART en Espera 🚨 - ${cleanPayload.clienteNombre} ${cleanPayload.clienteApellido}`.trim(),
                fechaCreacion: cleanPayload.fechaCarga,
                numeroCotizacion: cleanPayload.numeroCotizacion,
                tipoGestion: cleanPayload.tipoGestion,
                afiliacion: cleanPayload.afiliacion ? 'SÍ' : 'NO',
                clienteApellido: cleanPayload.clienteApellido,
                clienteNombre: cleanPayload.clienteNombre,
                clienteCuit: cleanPayload.clienteCuit,
                clienteDni: cleanPayload.clienteDni,
                canal: cleanPayload.canal,
                canalNombre: cleanPayload.canalNombre,
                subcanalNombre: cleanPayload.subcanalNombre,
                vendedor: cleanPayload.vendedor,
                capitas: cleanPayload.capitas,
                masaSalarial: cleanPayload.masaSalarial,
                alicuotaActual: cleanPayload.alicuotaActual,
                artActual: cleanPayload.artActual,
                nContratoOrigen: cleanPayload.nContratoOrigen
            };

            try {
                await fetch(API_URL, {
                    method: 'POST',
                    mode: 'no-cors',
                    headers: { 
                        'Content-Type': 'text/plain; charset=utf-8' 
                    },
                    body: JSON.stringify(emailPayload)
                });
            } catch (err) {
                console.error("Error enviando el correo de alerta:", err);
            }

            // 3. Manejo de cierre seguro sin pantalla en blanco
            if (typeof onSuccess === 'function') {
                onSuccess('Pedido de cotización registrado y enviado correctamente.');
            } else if (typeof onCancel === 'function') {
                onCancel();
            } else {
                navigate(-1);
            }

        } catch (error) {
            console.error("Error procesando solicitud de cotización:", error);
            setErrorMsg(`No se pudo procesar el pedido: ${error.message}`);
            setIsSubmitting(false);
        }
    };

    const handleCancelClick = () => {
        if (typeof onCancel === 'function') {
            onCancel();
        } else {
            navigate(-1);
        }
    };

    return (
        <div className="bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden animate-fade-in max-w-5xl mx-auto text-left">
            <div className="bg-orange-50 px-8 py-6 border-b border-orange-100 flex flex-col md:flex-row justify-between items-start md:items-center gap-6 text-left">
                <div className="flex items-center gap-4 text-left">
                    <button type="button" onClick={handleCancelClick} className="p-2 hover:bg-white/50 rounded-full transition-colors text-orange-800 border border-transparent hover:border-orange-200 cursor-pointer">
                        <IconArrowLeft />
                    </button>
                    <div className="text-left">
                        <h2 className="text-xl font-bold text-orange-900 flex items-center gap-2 uppercase tracking-tighter">
                            Solicitar Cotización ART
                            <span className="text-sm font-normal text-orange-700 bg-orange-100 px-2 py-0.5 rounded-md border border-orange-200 uppercase tracking-widest">
                                {isProspect ? 'Prospecto' : 'Cliente'}
                            </span>
                        </h2>
                        <p className="font-bold text-lg text-orange-800 uppercase mt-1">
                            {activeClient?.nombre} {activeClient?.apellido} <span className="mx-2 text-orange-300">|</span> <span className="font-mono text-sm">{activeClient?.cuit || activeClient?.dni}</span>
                        </p>
                    </div>
                </div>
            </div>

            <form onSubmit={handleSubmit} className="p-8 space-y-8 text-left">
                {errorMsg && (
                    <div className="bg-red-50 border border-red-200 text-red-700 px-6 py-4 rounded-xl font-medium text-sm flex items-center gap-3 animate-fade-in">
                        <span className="w-2 h-2 rounded-full bg-red-600 animate-pulse"></span>
                        {errorMsg}
                    </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 bg-gray-50 p-4 rounded-lg border border-gray-200 text-left">
                    <div className="text-left">
                        <label className="block text-xs font-bold text-gray-500 mb-1 uppercase tracking-tighter text-left">N° Cotización</label>
                        <input type="text" value={formData.numeroCotizacion} readOnly className="input-round bg-white font-mono text-center font-bold text-orange-600 border-orange-200 shadow-none cursor-default" />
                    </div>
                    <div className="text-left">
                        <label className="block text-xs font-bold text-gray-500 mb-1 uppercase tracking-tighter text-left">Fecha Carga</label>
                        <input type="date" name="fechaCarga" value={formData.fechaCarga} onChange={handleChange} className="input-round text-left" required />
                    </div>
                    <div className="text-left">
                        <label className="block text-xs font-bold text-gray-500 mb-1 uppercase tracking-tighter text-left">Estado General</label>
                        <input type="text" value={formData.estado} readOnly className="input-round bg-white font-bold text-orange-700 border-orange-200 shadow-none cursor-default text-center" />
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-left">
                    <div className="text-left">
                        <label className="block text-sm font-bold text-gray-600 mb-1 text-left">Tipo de Gestión *</label>
                        <div className="flex items-center gap-4">
                            <select name="tipoGestion" value={formData.tipoGestion} onChange={handleChange} className="input-round font-bold text-left border-slate-200 flex-1 cursor-pointer" required>
                                <option value="">Seleccione...</option>
                                <option value="Venta Nueva">Venta Nueva</option>
                                {!isProspect && <option value="Recotización de Cliente">Recotización de Cliente</option>}
                            </select>

                            {formData.tipoGestion === 'Venta Nueva' && (
                                <label className="flex items-center gap-2 bg-orange-50/80 border border-orange-200 px-3 py-2.5 rounded-xl cursor-pointer hover:bg-orange-100/50 transition-colors shrink-0 animate-fade-in">
                                    <input 
                                        type="checkbox" 
                                        name="afiliacion" 
                                        checked={Boolean(formData.afiliacion)} 
                                        onChange={handleChange} 
                                        className="w-4 h-4 text-orange-600 border-gray-300 rounded focus:ring-orange-500 cursor-pointer"
                                    />
                                    <span className="text-xs font-extrabold text-orange-900 uppercase tracking-wider select-none">
                                        Afiliación
                                    </span>
                                </label>
                            )}
                        </div>
                    </div>

                    <div className="text-left">
                        <label className="block text-sm font-bold text-gray-600 mb-1 text-left">Vendedor / Responsable *</label>
                        <select name="vendedor" value={formData.vendedor} onChange={handleChange} className="input-round border-slate-200 font-bold text-left cursor-pointer" required>
                            <option value="">Seleccione...</option>
                            {vendedores.map(v => <option key={v} value={v}>{v}</option>)}
                        </select>
                    </div>

                    {formData.tipoGestion === 'Recotización de Cliente' && (
                        <div className="md:col-span-2 animate-fade-in text-left">
                            <label className="block text-xs font-black text-blue-600 mb-1 uppercase tracking-widest text-left pl-1">
                                Contrato Vinculado de Origen *
                            </label>
                            {isLoadingContratos ? (
                                <div className="input-round bg-blue-50/30 border-blue-100 flex items-center px-4 text-blue-400 text-xs italic">
                                    <svg className="animate-spin h-4 w-4 mr-2 text-blue-500" fill="none" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                    </svg>
                                    Cargando contratos del cliente...
                                </div>
                            ) : listaContratosCliente.length === 0 ? (
                                <div className="input-round bg-red-50/50 border-red-200 flex items-center px-4 text-red-500 text-xs italic font-bold">
                                    El cliente no posee ningún contrato activo cargado para poder recotizar.
                                </div>
                            ) : (
                                <select
                                    name="contratoOrigenId"
                                    value={formData.contratoOrigenId || ''}
                                    onChange={handleChange}
                                    className="input-round border-blue-200 font-bold text-blue-800 bg-blue-50/10 focus:border-blue-400 focus:bg-white text-left animate-fade-in cursor-pointer"
                                    required
                                >
                                    <option value="">Seleccione un contrato...</option>
                                    {listaContratosCliente.map(c => (
                                        <option key={c.id} value={c.id}>
                                            Contrato #{c.nContrato} - {c.compania || 'S/D'} (Alic: {c.alicuota || '0'}%)
                                        </option>
                                    ))}
                                </select>
                            )}
                        </div>
                    )}
                </div>

                <fieldset className="border border-orange-200 rounded-xl p-6 relative mt-4 bg-orange-50/5 italic text-left">
                    <legend className="px-3 text-lg font-bold text-orange-700 bg-white ml-4 rounded-lg text-left">Detalles del Pedido (Riesgo)</legend>
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-5 pt-2 text-left items-end">
                        <div className="text-left">
                            <label className="label-form text-[10px] font-black text-gray-400 uppercase tracking-widest mb-1 text-left">CIIU (Opcional)</label>
                            <input type="text" name="ciiu" value={formData.ciiu} onChange={(e) => setFormData(prev => ({ ...prev, ciiu: e.target.value.replace(/\D/g, '') }))} className="input-round font-mono text-gray-800 border-slate-200 text-left" placeholder="Código" />
                        </div>
                        <div className="text-left">
                            <label className="label-form text-[10px] font-black text-gray-400 uppercase mb-1 tracking-widest text-center">Cápitas *</label>
                            <input type="text" name="capitas" value={formData.capitas} onChange={(e) => setFormData(prev => ({ ...prev, capitas: e.target.value.replace(/\D/g, '') }))} className="input-round border-slate-200 text-center font-bold" placeholder="0" required />
                        </div>
                        <div className="md:col-span-2 text-left">
                            <CurrencyInput 
                                label="Masa Salarial ($) *"
                                value={formData.masaSalarial}
                                onChange={(e) => handleCurrencyChange(e, 'masaSalarial')}
                                focusColor="focus-within:border-orange-500"
                            />
                        </div>
                        <div className="md:col-span-1 text-left">
                            <PercentInput 
                                label="Alic. Actual (%) *"
                                name="alicuotaActual"
                                value={formData.alicuotaActual}
                                onChange={handlePercentChange}
                                focusColor="focus-within:border-orange-500"
                            />
                        </div>
                        <div className="md:col-span-3 text-left">
                            <label className="label-form text-sm font-bold text-gray-600 mb-1 text-left">Compañía ART Actual</label>
                            <select name="artActual" value={formData.artActual} onChange={handleChange} className="input-round border-slate-200 uppercase font-bold text-blue-700 text-left cursor-pointer">
                                <option value="">Seleccione aseguradora...</option>
                                {companiasART.map(c => <option key={c.id} value={c.nombre}>{c.nombre}</option>)}
                            </select>
                        </div>
                    </div>
                </fieldset>

                <div className="pt-4 text-left">
                    <label className="block text-sm font-bold text-gray-600 mb-2 text-left">Observaciones a tener en cuenta</label>
                    <textarea name="observaciones" rows="4" value={formData.observaciones} onChange={handleChange} className="input-round border-slate-200 h-auto min-h-[120px] resize-none p-4 text-sm leading-relaxed text-left" placeholder="Detalles de siniestralidad, reclamos o pautas de negociación necesarias..."></textarea>
                </div>

                <div className="flex justify-end gap-4 pt-6 border-t border-gray-100 text-left">
                    <button type="button" onClick={handleCancelClick} className="px-6 py-3 rounded-xl border border-gray-300 font-bold text-gray-500 hover:bg-slate-50 uppercase text-[10px] tracking-widest transition shadow-sm text-left cursor-pointer">Cancelar</button>
                    <button type="submit" disabled={isSubmitting} className="px-8 py-3 rounded-lg bg-orange-600 text-white font-bold hover:bg-orange-700 shadow-lg disabled:opacity-50 uppercase text-[10px] tracking-widest transform hover:scale-105 transition-all shadow-orange-200 text-left flex items-center gap-2 cursor-pointer">
                        {isSubmitting ? 'Procesando...' : (
                            <>
                                <IconSend /> Confirmar Pedido
                            </>
                        )}
                    </button>
                </div>
            </form>

            <style>{`
                .input-round { width: 100%; padding: 0.75rem 1rem; border-radius: 0.75rem; border: 1px solid #e5e7eb; outline: none; transition: all 0.2s; font-size: 0.95rem; background-color: #fff; height: 46px; text-align: left; }
                .input-round:focus { border-color: #f97316; box-shadow: 0 0 0 3px rgba(249, 115, 22, 0.1); }
                .animate-fade-in { animation: fadeIn 0.4s ease-out forwards; }
                @keyframes fadeIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
            `}</style>
        </div>
    );
};

export default FormularioPedidoCotizacionART;